import { useEffect, useState } from 'react';
import { useCart } from '@v2/shared/lib/cart';
import { trackAddToCart, trackViewItem } from '@v2/shared/lib/analytics';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import MainPanel from './MainPanel';
import FeaturesPanel from './FeaturesPanel';
import BackIssuesPanel from './BackIssuesPanel';
import TitlesPanel from './TitlesPanel';
import StartTodayPanel from './StartTodayPanel';
import FaqPanel from './FaqPanel';
import ProductReviewsPanel from '@v2/shared/components/ProductReviewsPanel';
import { Props } from './types';

/**
 * The Zinio Unlimited landing page, previously served by headless_jshop at
 * /zinio-unlimited.
 *
 * A product page in disguise, like its Magzter Gold sibling: the price, the star rating
 * and the review widget all come from the one product ZinioUnlimitedPagePropsBuilder
 * resolves. The panels stack in the order they did at the source.
 *
 * Add-to-cart lives here rather than in the two panels that offer it, because it is one
 * action with two buttons — which is how headless had it, and what keeps the hero and
 * StartTodayPanel from disagreeing about what "Start free 1-month trial" does.
 *
 * No offer code is sent. The free month comes from the product's own auto-apply code,
 * which `cartAddItem()` applies server-side to anything added through it; headless's
 * `addItem(product.base_sku.code)` sent none either. See ZinioUnlimitedPagePropsBuilder.
 *
 * No <Container> here, and Layouts.FullWide rather than Layouts.Full for the same
 * reason: the bands are full-bleed and each panel containers its own contents, so the
 * one thing this page must not have is a Container around all of it.
 *
 * The "view all magazines" page at /categories/zinio-unlimited has deliberately not
 * moved and is still served by headless — every link to it here is an ordinary outbound
 * link.
 */
export default function ZinioUnlimited({ product }: Props) {
  const { addItem } = useCart();
  const { buildUrl } = useUrlGenerator();
  const [isAdding, setIsAdding] = useState(false);

  const sku = product.base_sku?.code;

  // A product page in disguise reports a product view like one. On mount and not on the
  // cart settling: `trackEvent` holds it behind the visitor's context by itself, so there
  // is nothing here to wait for.
  useEffect(() => {
    trackViewItem(product);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function addToCart() {
    if (!sku) {
      return;
    }

    setIsAdding(true);

    try {
      const cart = await addItem(sku);
      const added = cart.items?.[cart.items.length - 1];

      // Reported from the line the cart actually holds, before the navigation below —
      // landing on /cart does not report it, because the legacy page's `add_to_cart` only
      // covers lines added through its own form.
      if (added) {
        trackAddToCart(added);
      }

      // A full navigation rather than a router push: there is no router here, and the
      // cart is a legacy page that has to render server-side regardless.
      window.location.href = buildUrl('/cart');
    } catch (error) {
      console.error('Could not add Zinio Unlimited to the cart', error);
      // Only on the failure path. On the success path the navigation is already
      // underway, and clearing it would flash the buttons back to their resting state
      // while the visitor waits for the cart.
      setIsAdding(false);
    }
  }

  return (
    <>
      <MainPanel product={product} addToCart={addToCart} isAdding={isAdding} />
      <FeaturesPanel />
      <BackIssuesPanel />
      <TitlesPanel />
      <StartTodayPanel product={product} addToCart={addToCart} isAdding={isAdding} />
      <FaqPanel />
      <ProductReviewsPanel product={product} />
    </>
  );
}

ZinioUnlimited.layoutName = 'Layouts.FullWide';
